<template>
  <PanelMenu
    v-model:expandedKeys="expandedKeysLocal"
    :model="items"
    :class="['spm-panel-menu', `spm-panel-menu--${context}`]"
  >
    <template #item="{ item }">
      <div v-if="!item.hide">
        <router-link
          v-if="item.to"
          v-slot="{ navigate }"
          :to="item.to"
          custom
        >
          <div
            class="spm-panel-menu__item"
            @click="navigate"
          >
            <span class="spm-panel-menu__item-content">
              <i
                v-if="item.icon"
                class="spm-panel-menu__icon menu-icon mr-2 fa-fw"
                :class="item.icon"
                :title="item.label"
              />
              <span
                v-if="item.logo"
                class="menu-logo"
              >
                <img
                  :src="item.logo"
                  :alt="item.label"
                />
              </span>
              <span
                class="spm-panel-menu__label menu-label"
                :class="{ 'with-logo': item.logo, 'with-legend': item.legend }"
              >
                {{ item.label }}
                <span
                  v-if="item.legend"
                  class="menu-legend">
                  {{ item.legend }}
                </span>
              </span>
            </span>
          </div>
        </router-link>
        <SpmPanelMenuItemCommand
          v-else-if="item.callback || item.command"
          :item="item"
          :data="data"
        />
        <div v-else-if="item.separator">
          <div
            class="spm-panel-menu__separator p-menuitem-separator"
            role="separator"
          />
        </div>
        <div
          v-else
          class="spm-panel-menu__item"
          @click="item.commandRoot"
        >
          <span class="spm-panel-menu__item-content">
            <i
              v-if="item.icon"
              class="spm-panel-menu__icon menu-icon mr-2 fa-fw"
              :class="item.icon"
              :title="item.label"
            />
            <span
              v-if="item.logo"
              class="menu-logo"
            >
              <img
                :src="item.logo"
                :alt="item.label"
              />
            </span>
            <span
              class="spm-panel-menu__label menu-label"
              :class="{ 'with-logo': item.logo, 'with-legend': item.legend }"
            >
              {{ item.label }}
              <span
                v-if="item.legend"
                class="menu-legend">
                {{ item.legend }}
              </span>
            </span>
          </span>
          <span class="spm-panel-menu__chevron">
            <i
              class="right-icon-item fa-regular fa-angle-down"
              :class="{ 'itemExpanded': Object.keys(expandedKeysLocal).includes(item.key) }"
            />
          </span>
        </div>
      </div>
    </template>
  </PanelMenu>
</template>

<script lang="ts">
import {
  defineComponent, ref, Ref, SetupContext, watch,
} from 'vue';
import PanelMenu from 'primevue/panelmenu';
import SpmPanelMenuItemCommand from '@/components/spm-primevue/SpmPanelMenuItemCommand.vue';

export default defineComponent({
  name: 'SpmPanelMenu',

  components: {
    PanelMenu,
    SpmPanelMenuItemCommand,
  },

  props: {
    items: {
      type: Array,
      required: true,
    },

    expandedKeys: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    data: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    context: {
      type: String,
      required: false,
      default: 'default',
    },
  },

  emits: ['update:expandedKeys'],

  setup(props, { emit }: SetupContext) {
    const expandedKeysLocal: Ref<any> = ref(props.expandedKeys);

    watch(() => props.expandedKeys, () => {
      expandedKeysLocal.value = props.expandedKeys;
    });

    watch(expandedKeysLocal, () => {
      emit('update:expandedKeys', expandedKeysLocal.value);
    }, { deep: true });

    return {
      expandedKeysLocal,
    };
  },
});
</script>

<style lang="scss">
.spm-panel-menu {
  width: 100%;

  .p-panelmenu-panel {
    &:not(:last-child) {
      margin-bottom: 2px !important;
    }

    .p-panelmenu-header {
      cursor: pointer;

      &:not(.p-disabled):focus .p-panelmenu-header-content {
        outline: 0 none;
        outline-offset: 0;
        box-shadow: none !important;
      }

      .p-panelmenu-header-content {
        background-color: transparent !important;
        border: none !important;
        color: #4b5563 !important;
        font-size: 0.8125rem !important;
        border-radius: 8px !important;
        transition: all 0.15s ease;

        a {
          color: inherit !important;
        }

        &:hover {
          border-radius: 8px !important;
          background-color: #f5f6f8 !important;
        }

        .spm-panel-menu__item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem 0.625rem;
          gap: 0.5rem;
        }

        .spm-panel-menu__item-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 0;
        }

        .spm-panel-menu__icon {
          color: #9ca3af;
          font-size: 1.05rem;
          transition: color 0.15s ease;
          flex-shrink: 0;
        }

        .spm-panel-menu__label {
          line-height: 1.5rem;
          font-weight: 500;

          &.with-logo {
            line-height: 40px;
            display: inline-block;

            &.with-legend {
              line-height: normal;
            }
          }

          .menu-legend {
            display: block;
            font-size: 0.75rem;
            color: $heather;
          }
        }

        .spm-panel-menu__chevron {
          flex-shrink: 0;
        }

        span {
          i {
            font-size: 1.05rem;
          }
          line-height: 1.5rem;

          &.with-logo {
            line-height: 40px;
            display: inline-block;

            &.with-legend {
              line-height: normal;
            }
          }

          & .menu-legend {
            display: block;
            font-size: 0.75rem;
            color: $heather;
          }

          &.menu-logo {
            display: inline-block;
            width: 40px;
            height: 40px;
            margin-right: 10px;
            vertical-align: top;
            border-radius: 50%;
            background-color: #fff;
            position: relative;

            & > img {
              width: 100%;
              height: auto;
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              border-radius: 50%;
            }
          }
        }
      }
    }

    &.activeMenuItem {
      .p-panelmenu-header {
        .p-panelmenu-header-content {
          border-radius: 8px !important;
          background-color: rgba(148, 200, 64, 0.08) !important;
          color: #4a7a12 !important;
          font-weight: 600;

          .spm-panel-menu__icon,
          .menu-icon {
            color: $brand-color-primary !important;
          }

          a {
            color: #4a7a12 !important;
          }
        }
      }
    }

    .p-toggleable-content {
      padding-left: 0.5rem;

      .p-panelmenu-content {
        background-color: transparent;
        padding: 0.25rem 0 !important;
        padding-left: 0 !important;
        border: none !important;
        color: inherit !important;

        .p-menuitem {
          cursor: pointer;

          .p-menuitem-content {
            background-color: transparent !important;
            border: none !important;
            color: #4b5563 !important;
            margin-bottom: 1px;
            font-size: 0.8125rem !important;
            border-radius: 8px !important;
            transition: all 0.15s ease;

            a {
              color: inherit !important;
            }

            &:hover {
              border-radius: 8px !important;
              background-color: #f5f6f8 !important;
            }

            .spm-panel-menu__item {
              display: flex;
              align-items: center;
              padding: 0.5rem 0.625rem;
              gap: 0.5rem;
            }

            .spm-panel-menu__item-content {
              display: flex;
              align-items: center;
              gap: 0.5rem;
            }

            .spm-panel-menu__icon {
              color: #9ca3af;
              font-size: 1.05rem;
              flex-shrink: 0;
            }

            .spm-panel-menu__label {
              line-height: 1.5rem;
              font-weight: 500;
            }

            span {
              i {
                font-size: 1.05rem;
              }
              line-height: 1.5rem;

              &.with-logo {
                line-height: 40px;
                display: inline-block;

                &.with-legend {
                  line-height: normal;
                }
              }

              & .menu-legend {
                display: block;
                font-size: 0.75rem;
                color: $heather;
              }

              &.menu-logo {
                display: inline-block;
                width: 40px;
                height: 40px;
                margin-right: 10px;
                vertical-align: top;
                border-radius: 50%;
                background-color: #fff;
                position: relative;

                & > img {
                  width: 100%;
                  height: auto;
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  border-radius: 50%;
                }
              }
            }
          }

          &.activeMenuItem {
            .p-menuitem-content {
              border-radius: 8px !important;
              background-color: rgba(148, 200, 64, 0.08) !important;
              color: #4a7a12 !important;
              font-weight: 600;

              a {
                color: #4a7a12 !important;
              }
            }
          }
        }
      }
    }

    .right-icon-item {
      color: #9ca3af;
      transition: transform ease-in-out 0.2s;

      &.itemExpanded {
        transform: rotate(180deg);
      }
    }
  }

  .menu-label-star--premium {
    height: 26px;
    width: 26px;
    color: $silver;
  }
  .menu-label-star--ultimate {
    height: 26px;
    width: 26px;
    color: $gold;
  }
}

// Override for overlay panels — tighter padding
.spm-overlay-panel {
  .spm-panel-menu {
    .p-panelmenu-panel {
      .p-panelmenu-header .p-panelmenu-header-content {
        color: #374151 !important;

        .spm-panel-menu__item {
          padding: 0.4375rem 0.5rem;
        }

        &:hover {
          background-color: #f5f6f8 !important;
        }

        .spm-panel-menu__icon,
        .menu-icon {
          color: #6b7280;
        }
      }

      &.activeMenuItem .p-panelmenu-header .p-panelmenu-header-content {
        background-color: rgba(148, 200, 64, 0.08) !important;
        color: #4a7a12 !important;

        .spm-panel-menu__icon,
        .menu-icon {
          color: $brand-color-primary !important;
        }
      }

      .p-toggleable-content .p-panelmenu-content .p-menuitem {
        .p-menuitem-content {
          color: #374151 !important;

          .spm-panel-menu__item {
            padding: 0.4375rem 0.5rem;
          }

          &:hover {
            background-color: #f5f6f8 !important;
          }
        }

        &.activeMenuItem .p-menuitem-content {
          background-color: rgba(148, 200, 64, 0.08) !important;
          color: #4a7a12 !important;
        }
      }
    }
  }
}

.spm-panel-menu__separator,
.p-menuitem-separator {
  border-top: 1px solid #f0f0f0;
  margin: 0.25rem 0;
}

@keyframes custom-spinner-color {
  100%, 0% {
    stroke: grey;
  }
  40% {
    stroke: grey;
  }
  66% {
    stroke: grey;
  }
  80%, 90% {
    stroke: grey;
  }
}

@-webkit-keyframes custom-spinner-color {
  100%, 0% {
    stroke: grey;
  }
  40% {
    stroke: grey;
  }
  66% {
    stroke: grey;
  }
  80%, 90% {
    stroke: grey;
  }
}

.p-progress-spinner {
  margin: unset !important;
}

.p-progress-spinner-circle {
  stroke-dasharray: 89, 200;
  stroke-dashoffset: 0;
  stroke: grey;
  animation: p-progress-spinner-dash 1.5s ease-in-out infinite, custom-spinner-color 6s ease-in-out infinite !important;
  stroke-linecap: round;
}
</style>
